
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { COUNTRIES, CATEGORIES, SERVICES } from '../data';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileDropdown, setMobileDropdown] = useState<string | null>(null);
  const navigate = useNavigate();

  const toggleMobileDropdown = (id: string) => {
    setMobileDropdown(mobileDropdown === id ? null : id);
  };

  return (
    <nav className="sticky top-0 z-50 bg-white text-brand-deep shadow-md border-b border-brand-surface">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center space-x-2">
            <span className="text-2xl font-black tracking-tighter text-brand-deep">LEC TRAVEL</span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <div 
              className="relative group"
              onMouseEnter={() => setActiveDropdown('celojumi')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <Link to="/celojumi" className="flex items-center hover:text-brand-accent transition-colors py-2 font-bold uppercase text-xs tracking-widest">
                Ceļojumi <i className="fas fa-chevron-down ml-2 text-[10px]"></i>
              </Link>
              {activeDropdown === 'celojumi' && (
                <div className="absolute top-full left-0 w-64 bg-white text-black shadow-2xl rounded-b-xl border-t-2 border-brand-accent overflow-hidden">
                  <div className="py-4 grid grid-cols-1">
                    {CATEGORIES.map(cat => (
                      <Link 
                        key={cat} 
                        to={`/celojumi?category=${encodeURIComponent(cat)}`}
                        className="px-6 py-2 hover:bg-brand-surface hover:text-brand-accent transition-colors font-bold text-sm text-black"
                        onClick={() => setActiveDropdown(null)}
                      >
                        {cat}
                      </Link>
                    ))}
                  </div>
                  <div className="bg-brand-surface p-4 text-center border-t border-brand-surface">
                    <Link 
                      to="/celojumi" 
                      className="inline-flex items-center gap-2 text-brand-accent font-black text-xs uppercase tracking-widest hover:text-brand-accentHover transition-colors group"
                      onClick={() => setActiveDropdown(null)}
                    >
                      Visi ceļojumi <i className="fas fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform"></i>
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <div 
              className="relative group"
              onMouseEnter={() => setActiveDropdown('galamerki')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <Link to="/galamerki" className="flex items-center hover:text-brand-accent transition-colors py-2 font-bold uppercase text-xs tracking-widest">
                Galamērķi <i className="fas fa-chevron-down ml-2 text-[10px]"></i>
              </Link>
              {activeDropdown === 'galamerki' && (
                <div className="absolute top-full left-0 w-80 bg-white text-black shadow-2xl rounded-b-xl border-t-2 border-brand-accent overflow-hidden">
                  <div className="py-4 max-h-[400px] overflow-y-auto no-scrollbar">
                    {COUNTRIES.map(country => (
                      <div key={country.id} className="px-6 py-3 border-b border-brand-surface last:border-0">
                        <Link 
                          to={`/galamerki/${country.slug}`} 
                          className="font-black text-black hover:text-brand-accent text-sm block mb-1 transition-colors"
                          onClick={() => setActiveDropdown(null)}
                        >
                          {country.name}
                        </Link>
                        <div className="pl-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-black uppercase tracking-wider">
                          {country.cities.map(city => (
                            <Link 
                              key={city.id} 
                              to={`/celojumi?destination=${encodeURIComponent(city.name)}`} 
                              className="hover:text-brand-accent transition-colors font-black"
                              onClick={() => setActiveDropdown(null)}
                            >
                              {city.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="bg-brand-surface p-4 text-center border-t border-brand-surface">
                    <Link 
                      to="/galamerki" 
                      className="inline-flex items-center gap-2 text-brand-accent font-black text-xs uppercase tracking-widest hover:text-brand-accentHover transition-colors group"
                      onClick={() => setActiveDropdown(null)}
                    >
                      Apskatīt visu <i className="fas fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform"></i>
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <div 
              className="relative group"
              onMouseEnter={() => setActiveDropdown('pakalpojumi')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <Link to="/pakalpojumi" className="flex items-center hover:text-brand-accent transition-colors py-2 font-bold uppercase text-xs tracking-widest">
                Pakalpojumi <i className="fas fa-chevron-down ml-2 text-[10px]"></i>
              </Link>
              {activeDropdown === 'pakalpojumi' && (
                <div className="absolute top-full right-0 md:left-0 w-80 md:w-[450px] bg-white text-black shadow-2xl rounded-b-xl border-t-2 border-brand-accent overflow-hidden">
                  <div className="py-6 px-4 grid grid-cols-1 gap-2">
                    {SERVICES.map(service => (
                      <Link 
                        key={service.id} 
                        to={`/pakalpojumi/${service.slug}`}
                        className="flex flex-col p-4 hover:bg-brand-surface rounded-lg transition-all group"
                        onClick={() => setActiveDropdown(null)}
                      >
                        <div>
                          <h4 className="font-black text-sm text-black transition-colors">{service.title}</h4>
                          <p className="text-[10px] font-medium text-black line-clamp-1 mt-1">{service.shortDescription}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                  <div className="bg-brand-surface p-4 text-center border-t border-brand-surface">
                    <Link 
                      to="/pakalpojumi" 
                      className="inline-flex items-center gap-2 text-black font-black text-xs uppercase tracking-widest hover:text-brand-accent transition-colors group"
                      onClick={() => setActiveDropdown(null)}
                    >
                      Visi pakalpojumi <i className="fas fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform"></i>
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link to="/blogs" className="hover:text-brand-accent transition-colors font-bold uppercase text-xs tracking-widest">Blogs</Link>
            <Link to="/par-mums" className="hover:text-brand-accent transition-colors font-bold uppercase text-xs tracking-widest">Par mums</Link>
            <Link to="/kontakti" className="hover:text-brand-accent transition-colors font-bold uppercase text-xs tracking-widest">Kontakti</Link>

            <button 
              onClick={() => navigate('/celojumi')}
              className="bg-brand-accent text-white px-6 py-2 rounded-md font-black text-xs uppercase tracking-widest hover:bg-brand-accentHover transition-all shadow-lg shadow-orange-100"
            >
              Doties Ceļojumā
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 text-brand-deep">
              <i className={`fas ${isMenuOpen ? 'fa-times' : 'fa-bars'} text-2xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden fixed inset-0 top-20 bg-white z-[60] py-8 px-8 overflow-y-auto no-scrollbar animate-fade-in flex flex-col">
          <div className="space-y-6 flex-grow">
            {/* Ceļojumi Accordion */}
            <div>
              <button 
                onClick={() => toggleMobileDropdown('celojumi')}
                className="w-full flex justify-between items-center py-3 font-black text-brand-deep uppercase text-lg tracking-widest"
              >
                Ceļojumi <i className={`fas fa-chevron-down text-xs transition-transform ${mobileDropdown === 'celojumi' ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileDropdown === 'celojumi' && (
                <div className="pl-6 pt-2 space-y-4 animate-fade-in">
                  {CATEGORIES.map(cat => (
                    <Link 
                      key={cat} 
                      to={`/celojumi?category=${encodeURIComponent(cat)}`} 
                      onClick={() => setIsMenuOpen(false)} 
                      className="block text-sm font-bold text-black"
                    >
                      {cat}
                    </Link>
                  ))}
                  <Link to="/celojumi" onClick={() => setIsMenuOpen(false)} className="block text-sm font-black text-brand-accent uppercase tracking-widest">Visi ceļojumi</Link>
                </div>
              )}
            </div>

            {/* Galamērķi Accordion */}
            <div>
              <button 
                onClick={() => toggleMobileDropdown('galamerki')}
                className="w-full flex justify-between items-center py-3 font-black text-brand-deep uppercase text-lg tracking-widest"
              >
                Galamērķi <i className={`fas fa-chevron-down text-xs transition-transform ${mobileDropdown === 'galamerki' ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileDropdown === 'galamerki' && (
                <div className="pl-6 pt-2 space-y-4 animate-fade-in">
                  {COUNTRIES.map(c => (
                    <Link 
                      key={c.id} 
                      to={`/galamerki/${c.slug}`} 
                      onClick={() => setIsMenuOpen(false)} 
                      className="block text-sm font-bold text-black"
                    >
                      {c.name}
                    </Link>
                  ))}
                  <Link to="/galamerki" onClick={() => setIsMenuOpen(false)} className="block text-sm font-black text-brand-accent uppercase tracking-widest">Visi galamērķi</Link>
                </div>
              )}
            </div>

            {/* Pakalpojumi Accordion */}
            <div>
              <button 
                onClick={() => toggleMobileDropdown('pakalpojumi')}
                className="w-full flex justify-between items-center py-3 font-black text-brand-deep uppercase text-lg tracking-widest"
              >
                Pakalpojumi <i className={`fas fa-chevron-down text-xs transition-transform ${mobileDropdown === 'pakalpojumi' ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileDropdown === 'pakalpojumi' && (
                <div className="pl-6 pt-2 space-y-5 animate-fade-in">
                  {SERVICES.map(s => (
                    <Link 
                      key={s.id} 
                      to={`/pakalpojumi/${s.slug}`} 
                      onClick={() => setIsMenuOpen(false)} 
                      className="block text-sm font-bold text-black leading-tight"
                    >
                      {s.title}
                    </Link>
                  ))}
                  <Link to="/pakalpojumi" onClick={() => setIsMenuOpen(false)} className="block text-sm font-black text-black uppercase tracking-widest">Visi pakalpojumi</Link>
                </div>
              )}
            </div>

            <Link to="/par-mums" onClick={() => setIsMenuOpen(false)} className="block py-3 font-black text-brand-deep uppercase text-lg tracking-widest">Par mums</Link>
            <Link to="/kontakti" onClick={() => setIsMenuOpen(false)} className="block py-3 font-black text-brand-deep uppercase text-lg tracking-widest">Kontakti</Link>
          </div>
          
          <div className="pt-10 pb-6">
            <button 
              onClick={() => { navigate('/celojumi'); setIsMenuOpen(false); }}
              className="w-full bg-brand-accent text-white py-5 rounded-lg font-black uppercase text-base tracking-[0.2em] shadow-2xl shadow-orange-100"
            >
              Doties Ceļojumā
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

const Footer: React.FC = () => {
  return (
    <footer className="bg-brand-deep text-white py-20 mt-auto">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-12">
        {/* Branding Column */}
        <div className="lg:col-span-1">
          <h3 className="text-2xl font-black mb-6 text-white tracking-tighter">LEC TRAVEL</h3>
          <p className="text-white/70 leading-relaxed font-bold text-sm">
            Mēs radām neaizmirstamus mirkļus un palīdzam atklāt pasaules skaistākās vietas ar kvalitāti un rūpēm.
          </p>
        </div>

        {/* Galvenā izvēlne Column */}
        <div>
          <h4 className="font-black mb-8 text-xs uppercase tracking-[0.2em] text-brand-accent">Galvenā izvēlne</h4>
          <ul className="space-y-4 font-bold text-sm">
            <li><Link to="/celojumi" className="hover:text-brand-accent transition-all">Ceļojumi</Link></li>
            <li><Link to="/galamerki" className="hover:text-brand-accent transition-all">Galamērķi</Link></li>
            <li><Link to="/pakalpojumi" className="hover:text-brand-accent transition-all">Pakalpojumi</Link></li>
            <li><Link to="/blogs" className="hover:text-brand-accent transition-all">Blogs</Link></li>
            <li><Link to="/par-mums" className="hover:text-brand-accent transition-all">Par mums</Link></li>
            <li><Link to="/kontakti" className="hover:text-brand-accent transition-all">Kontakti</Link></li>
          </ul>
        </div>

        {/* Galamērķi Column */}
        <div>
          <h4 className="font-black mb-8 text-xs uppercase tracking-[0.2em] text-brand-accent">Galamērķi</h4>
          <ul className="space-y-4 font-bold text-sm">
            {COUNTRIES.map(country => (
              <li key={country.id}>
                <Link to={`/galamerki/${country.slug}`} className="hover:text-brand-accent transition-all">
                  {country.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Kontakti Column */}
        <div>
          <h4 className="font-black mb-8 text-xs uppercase tracking-[0.2em] text-brand-accent">Kontakti</h4>
          <ul className="space-y-4 font-bold text-sm">
            <li className="flex items-center gap-3"><i className="fas fa-map-marker-alt text-brand-accent w-5"></i> Rīga, Brīvības iela 123</li>
            <li className="flex items-center gap-3"><i className="fas fa-phone text-brand-accent w-5"></i> +371 20000000</li>
            <li className="flex items-center gap-3"><i className="fas fa-envelope text-brand-accent w-5"></i> info@lectravel.lv</li>
          </ul>
        </div>

        {/* Social Column */}
        <div>
          <h4 className="font-black mb-8 text-xs uppercase tracking-[0.2em] text-brand-accent">Sekojiet mums</h4>
          <div className="flex space-x-4">
            <a href="#" className="w-10 h-10 border border-white/20 text-white rounded-lg flex items-center justify-center hover:bg-brand-accent hover:border-brand-accent transition-all">
              <i className="fab fa-facebook-f text-sm"></i>
            </a>
            <a href="#" className="w-10 h-10 border border-white/20 text-white rounded-lg flex items-center justify-center hover:bg-brand-accent hover:border-brand-accent transition-all">
              <i className="fab fa-instagram text-sm"></i>
            </a>
            <a href="#" className="w-10 h-10 border border-white/20 text-white rounded-lg flex items-center justify-center hover:bg-brand-accent hover:border-brand-accent transition-all">
              <i className="fab fa-linkedin-in text-sm"></i>
            </a>
          </div>
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 mt-16 pt-8 border-t border-white/10 text-center text-white/30 text-[10px] font-black uppercase tracking-widest">
        &copy; {new Date().getFullYear()} LEC Travel. Visas tiesības aizsargātas.
      </div>
    </footer>
  );
};

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-grow">{children}</main>
      <Footer />
    </div>
  );
};
